import {
    IExpressionResolver, IExpressionResolution, failedExpressionResolution,
    ResolutionErrorKind, ParsingErrorKind, successfulExpressionResolution, IResolutionError
} from 'app/model/expressions/expression-resolution';
import { ScalarExpression, ExpressionCategory } from './expression';
import { Owned } from 'app/model/base/named';
import { buildSubExpressionResolver, findPattern } from 'app/model/expressions/expression-utils';

/**
 * Resolves expression string interpolation syntax @{...}
 */
export class StringInterpolationResolver implements IExpressionResolver {
    /**
     * Initializes a new instance of the StringInterpolationResolver class.
     * @param _entity The entity owning the field being parameterized.
     * @param _expressionResolversCallback Provides supported expression resolvers used to resolve expressions within the expression string.
     */
    public constructor(private readonly _entity: Owned, private readonly _expressionResolversCallback: () => IExpressionResolver[]) {
    }

    public isOfType(expression: string, ignoreStart?: boolean): boolean {
        // Regex matches a string that starts with '@{' or the substring '@{'
        // anywhere in the string that is not preceded by '@'.
        const interpolationRegex = /(?:^|[^@])@{/;
        return expression && interpolationRegex.test(expression);
    }

    public resolve(expression: string, ignoreStart?: boolean): IExpressionResolution {
        if (!this.isOfType(expression, ignoreStart)) {
            return failedExpressionResolution(expression, { kind: ResolutionErrorKind.Unrecognized, details: expression });
        }

        // find the parts enclosed by @{}
        let index = 0;
        const interpolationExpressions: Array<{ expression: string, offset: number }> = [];

        while (index > -1) {
            const interpolationExpressionStart = expression.indexOf('@{', index);
            index = interpolationExpressionStart;

            // The sequence '@@{' is a string literal so it can be ignored.
            if (index > 0 && expression[index - 1] === '@') {
                index += 2;
                continue;
            }

            if (interpolationExpressionStart > -1) {
                // ignore the leading @{
                const interpolationExpressionEnd = findPattern(expression, '}', interpolationExpressionStart + 2);

                if (interpolationExpressionEnd === -1) {
                    // the expression is not properly closed
                    return failedExpressionResolution(expression, {
                        kind: ResolutionErrorKind.InvalidSyntax,
                        parsingError: {
                            error: ParsingErrorKind.MissingClosingBrace,
                            startIndex: interpolationExpressionStart
                        }
                    });
                }

                const detectedInterpolationExpression = expression.substring(interpolationExpressionStart + 2, interpolationExpressionEnd);

                if (!detectedInterpolationExpression) {
                    // can't have an empty expression between @{}
                    return failedExpressionResolution(expression, {
                        kind: ResolutionErrorKind.EmptyInterpolationExpression,
                        startIndex: interpolationExpressionStart,
                        endIndex: interpolationExpressionEnd
                    });
                }

                interpolationExpressions.push({ expression: detectedInterpolationExpression, offset: interpolationExpressionStart + 2 });

                index = interpolationExpressionEnd + 1;
            }
        }

        const expressionDependencies: Owned[] = [];

        // resolve the expressions
        for (const subExpression of interpolationExpressions) {
            const resolution = buildSubExpressionResolver(this._entity, this._expressionResolversCallback)(subExpression.expression);

            if (!resolution.isValid) {
                if (resolution.error.startIndex !== undefined) {
                    resolution.error.startIndex += subExpression.offset;
                }

                if (resolution.error.endIndex !== undefined) {
                    resolution.error.endIndex += subExpression.offset;
                }

                if (resolution.error.parsingError) {
                    if (resolution.error.parsingError.startIndex !== undefined) {
                        resolution.error.parsingError.startIndex += subExpression.offset;
                    }

                    if (resolution.error.parsingError.endIndex !== undefined) {
                        resolution.error.parsingError.endIndex += subExpression.offset;
                    }
                }

                return resolution;
            } else {
                if (resolution.dependencies) {
                    expressionDependencies.push(...resolution.dependencies);
                }
            }
        }

        // all expressions are valid!
        return successfulExpressionResolution(
            expression,
            new ScalarExpression(
                ExpressionCategory.Interpolation, 'String interpolation expression', expression,
                'String interpolation expression', 'String'),
            expressionDependencies);
    }

    public checkApplicability(entity: Owned): { isApplicable: boolean, error?: IResolutionError } {
        return { isApplicable: true };
    }
}
